import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  Calendar, MapPin, Clock, Users, ChevronRight, 
  CheckCircle, ArrowLeft, Sparkles, Ticket, Star,
  Instagram, Youtube, Music2, Globe, Twitter, Linkedin, 
  Mail, Zap, ArrowUpRight, Heart, UserPlus, UserCheck, Share2
} from 'lucide-react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '../lib/api';
import { usePublicAuth } from '../context/PublicAuthContext';
import SEO from '../components/SEO';

// Social icon mapping with brand colors
const socialConfig = {
  instagram: { icon: Instagram, color: '#E4405F', label: 'Instagram' },
  youtube: { icon: Youtube, color: '#FF0000', label: 'YouTube' },
  spotify: { icon: Music2, color: '#1DB954', label: 'Spotify' },
  website: { icon: Globe, color: '#6366F1', label: 'Web' },
  tiktok: { icon: Music2, color: '#00F2EA', label: 'TikTok' },
  twitter: { icon: Twitter, color: '#1DA1F2', label: 'Twitter' },
  linkedin: { icon: Linkedin, color: '#0A66C2', label: 'LinkedIn' },
  email: { icon: Mail, color: '#EA4335', label: 'Email' },
};

// Marquee component for scrolling text
const Marquee = ({ children, speed = 30 }) => {
  return (
    <div className="overflow-hidden whitespace-nowrap">
      <motion.div
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: speed, repeat: Infinity, ease: 'linear' }}
        className="inline-flex"
      >
        {children}
        {children}
      </motion.div>
    </div>
  );
};

// Animated counter
const AnimatedNumber = ({ value, duration = 2 }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let start = 0;
    const end = parseInt(value) || 0;
    if (end === 0) {
      setCount(0);
      return;
    }
    const increment = end / (duration * 60);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);
    
    return () => clearInterval(timer);
  }, [value, duration]);
  
  return <span>{count}</span>;
};

const ProducerPublicPage = () => {
  const { slug } = useParams();
  const { user, isAuthenticated } = usePublicAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  useEffect(() => {
    fetchProducerData();
  }, [slug]);

  const fetchProducerData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/producers/public/${slug}`);
      setData(response.data);
      setIsFollowing(response.data.is_following || false);
      setFollowersCount(response.data.stats?.followers_count || 0);
    } catch (err) {
      console.error('Error fetching producer:', err);
      setError(err.response?.data?.detail || 'Productora no encontrada');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Iniciá sesión para seguir productoras');
      return;
    }
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        const res = await api.delete(`/producers/${data.producer.id}/follow`);
        setIsFollowing(false);
        setFollowersCount(res.data.followers_count);
        toast.success('Dejaste de seguir a esta productora');
      } else {
        const res = await api.post(`/producers/${data.producer.id}/follow`);
        setIsFollowing(true);
        setFollowersCount(res.data.followers_count);
        toast.success('Ahora seguís a esta productora');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleShare = async () => {
    // Use the share URL for social media (shows producer logo in preview)
    // Add timestamp to bust WhatsApp/Facebook cache
    const baseUrl = window.location.origin;
    const timestamp = Date.now();
    const shareUrl = `${baseUrl}/api/s/productora/${slug}?v=${timestamp}`;
    const producerName = data?.producer?.company_name || data?.producer?.business_name || 'Productora';
    
    const shareData = {
      title: producerName,
      text: `${producerName} - Productora de eventos`,
      url: shareUrl,
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(shareUrl);
          toast.success('Enlace copiado');
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Enlace copiado');
    }
  };

  // Memoize events filtering
  const { upcomingEvents, pastEvents } = useMemo(() => {
    if (!data?.events) return { upcomingEvents: [], pastEvents: [] };
    return {
      upcomingEvents: data.events.filter(e => e.status === 'published'),
      pastEvents: data.events.filter(e => e.status === 'completed')
    };
  }, [data?.events]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="relative">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 rounded-full border-2 border-white/20 border-t-[#D4AF37]"
          />
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <Zap size={16} className="text-[#D4AF37]" />
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white/5 flex items-center justify-center">
            <Users size={32} className="text-white/30" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Productora no encontrada</h1>
          <p className="text-white/40 mb-8 text-sm">{error}</p>
          <Link 
            to="/eventos"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full hover:bg-white/90 transition-all"
          >
            <ArrowLeft size={16} /> Ver eventos
          </Link>
        </motion.div>
      </div>
    );
  }

  const { producer, stats } = data;
  const branding = producer.branding || {};
  const primaryColor = branding.primary_color || '#D4AF37';
  const auraColor1 = branding.aura_color_1 || primaryColor;
  const auraColor2 = branding.aura_color_2 || '#FFD700';
  const auraColor3 = branding.aura_color_3 || '#FFA500';
  
  const currentEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  // SEO description for producer page
  const seoDescription = producer.description 
    ? producer.description.substring(0, 160) 
    : `${producer.company_name} - Productora de eventos. Descubrí sus próximos eventos y comprá tus entradas.`;

  return (
    <div ref={containerRef} className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      <SEO 
        title={producer.company_name}
        description={seoDescription}
        image={producer.logo_url}
        url={`/productora/${producer.slug}`}
        type="profile"
      />
      {/* Animated mesh gradient background */}
      <div className="fixed inset-0 pointer-events-none">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0">
          <div 
            className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[150px]"
            style={{ background: auraColor1 }}
          />
          <div 
            className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full opacity-15 blur-[120px]"
            style={{ background: auraColor2 }}
          />
          <div 
            className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-[100px]"
            style={{ background: auraColor3 }}
          />
        </motion.div>
        {/* Grain overlay */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link 
            to="/eventos"
            className="flex items-center gap-2 px-3 py-2 bg-white/5 backdrop-blur-xl rounded-full text-sm text-white/70 hover:text-white hover:bg-white/10 transition-all border border-white/10"
          >
            <ArrowLeft size={14} />
            <span className="hidden sm:inline">Eventos</span>
          </Link>
          
          <div className="flex items-center gap-2">
            {/* Share Button */}
            <motion.button
              onClick={handleShare}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all"
              data-testid="share-producer-btn"
              title="Compartir"
            >
              <Share2 size={16} />
            </motion.button>
            
            {/* Follow Button */}
            <motion.button
              onClick={handleFollow}
              disabled={followLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                isFollowing 
                  ? 'bg-white/10 border-white/20 text-white' 
                  : 'bg-[#D4AF37] border-[#D4AF37] text-black hover:bg-[#D4AF37]/90'
              }`}
              data-testid="follow-btn"
            >
              {followLoading ? (
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                />
              ) : isFollowing ? (
                <>
                  <UserCheck size={16} />
                  <span className="hidden sm:inline">Siguiendo</span>
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  <span className="hidden sm:inline">Seguir</span>
                </>
              )}
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Full width, immersive */}
      <section className="relative min-h-[75vh] sm:min-h-[85vh] md:min-h-[90vh] flex flex-col justify-end pt-16 sm:pt-20">
        {/* Logo floating animation */}
        <div className="absolute top-[18%] sm:top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            {/* Pulsing glow */}
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 rounded-full blur-3xl"
              style={{ background: primaryColor }}
            />
            
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={producer.business_name}
                className="relative w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 object-contain rounded-2xl sm:rounded-3xl"
              />
            ) : (
              <div 
                className="relative w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-2xl sm:rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center"
              >
                <span className="text-5xl sm:text-6xl md:text-7xl font-black" style={{ color: primaryColor }}>
                  {producer.business_name?.charAt(0) || 'P'}
                </span>
              </div>
            )}
          </motion.div>
        </div>

        {/* Main content */}
        <div className="relative z-10 px-4 pb-6 sm:pb-12 md:pb-20">
          <div className="max-w-7xl mx-auto">
            {/* Verified badge */}
            {producer.is_verified && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-4 sm:mb-6"
              >
                <CheckCircle size={12} className="text-emerald-400" />
                <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Verificada</span>
              </motion.div>
            )}

            {/* Name - Big and bold */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.85] mb-4 sm:mb-6"
            >
              {producer.business_name?.split(' ').map((word, i) => (
                <span 
                  key={i} 
                  className="block"
                  style={i === 0 ? { 
                    color: 'transparent',
                    WebkitTextStroke: `1.5px ${primaryColor}`,
                  } : {}}
                >
                  {word}
                </span>
              ))}
            </motion.h1>

            {/* Location and description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-8"
            >
              <div className="max-w-xl">
                {(producer.city || producer.province) && (
                  <p className="flex items-center gap-2 text-white/50 mb-2 sm:mb-4">
                    <MapPin size={14} />
                    <span className="text-xs sm:text-sm">{[producer.city, producer.province].filter(Boolean).join(', ')}</span>
                  </p>
                )}
                {producer.description && (
                  <p className="text-white/60 text-sm sm:text-base md:text-lg leading-relaxed line-clamp-3 sm:line-clamp-none">
                    {producer.description}
                  </p>
                )}
              </div>

              {/* Stats - Horizontal pills */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="px-4 sm:px-5 py-2 sm:py-3 bg-white/5 backdrop-blur-xl rounded-full border border-white/10">
                  <span className="text-xl sm:text-2xl md:text-3xl font-black" style={{ color: primaryColor }}>
                    <AnimatedNumber value={stats?.total_events || 0} />
                  </span>
                  <span className="text-white/40 text-[10px] sm:text-xs uppercase tracking-wider ml-1 sm:ml-2">eventos</span>
                </div>
                <div className="px-4 sm:px-5 py-2 sm:py-3 bg-white/5 backdrop-blur-xl rounded-full border border-white/10">
                  <span className="text-xl sm:text-2xl md:text-3xl font-black text-white">
                    <AnimatedNumber value={stats?.upcoming_events || 0} />
                  </span>
                  <span className="text-white/40 text-[10px] sm:text-xs uppercase tracking-wider ml-1 sm:ml-2">próximos</span>
                </div>
                <div className="px-4 sm:px-5 py-2 sm:py-3 bg-white/5 backdrop-blur-xl rounded-full border border-white/10">
                  <Heart size={16} className="inline mr-1 sm:mr-2" style={{ color: primaryColor }} />
                  <span className="text-xl sm:text-2xl md:text-3xl font-black text-white">
                    <AnimatedNumber value={followersCount} />
                  </span>
                  <span className="text-white/40 text-[10px] sm:text-xs uppercase tracking-wider ml-1 sm:ml-2">seguidores</span>
                </div>
              </div>
            </motion.div>

            {/* Social Links - Mobile */}
            {producer.social_links?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex items-center gap-2 mt-4"
              >
                {producer.social_links.map((link, idx) => {
                  const config = socialConfig[link.platform] || socialConfig.website;
                  const Icon = config.icon;
                  return (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all"
                    >
                      <Icon size={16} className="text-white/60" />
                    </a>
                  );
                })}
              </motion.div>
            )}
          </div>
        </div>

        {/* Marquee divider */}
        <div className="border-y border-white/10 py-3 bg-black/20 backdrop-blur-sm">
          <Marquee speed={40}>
            {[...Array(8)].map((_, i) => (
              <span key={i} className="flex items-center mx-8">
                <Star size={12} className="mr-3" style={{ color: primaryColor }} />
                <span className="text-sm font-bold uppercase tracking-widest text-white/30">
                  {producer.business_name}
                </span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Events Section */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section header with tabs */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-xs font-semibold uppercase tracking-[0.3em] mb-3"
                style={{ color: primaryColor }}
              >
                Experiencias
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-5xl font-black uppercase tracking-tight"
              >
                Eventos
              </motion.h2>
            </div>

            {/* Tabs */}
            <div className="flex p-1 bg-white/5 rounded-full border border-white/10">
              {[
                { id: 'upcoming', label: 'Próximos', count: upcomingEvents.length },
                { id: 'past', label: 'Pasados', count: pastEvents.length },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                    activeTab === tab.id 
                      ? 'text-black' 
                      : 'text-white/50 hover:text-white'
                  }`}
                >
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 rounded-full"
                      style={{ background: primaryColor }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10">{tab.label} ({tab.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Events Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {currentEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-6">
                    <Calendar size={24} className="text-white/30" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    {activeTab === 'upcoming' ? 'Sin eventos próximos' : 'Sin eventos pasados'}
                  </h3>
                  <p className="text-white/40 text-sm max-w-sm">
                    {activeTab === 'upcoming' 
                      ? 'Pronto anunciaremos nuevas fechas. ¡Seguinos para enterarte!' 
                      : 'Aún no hay historial de eventos'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {currentEvents.map((event, idx) => (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      onMouseEnter={() => setHoveredEvent(event.id)}
                      onMouseLeave={() => setHoveredEvent(null)}
                    >
                      <Link 
                        to={`/evento/${event.id}`}
                        className="group relative flex flex-col md:flex-row bg-white/[0.02] rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-500"
                      >
                        {/* Image */}
                        <div className="relative w-full md:w-48 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                          {event.image_url ? (
                            <motion.img 
                              src={event.image_url} 
                              alt={event.title}
                              className="w-full h-full object-cover"
                              animate={{ scale: hoveredEvent === event.id ? 1.08 : 1 }}
                              transition={{ duration: 0.6 }}
                            />
                          ) : (
                            <div 
                              className="w-full h-full flex items-center justify-center"
                              style={{ background: `linear-gradient(135deg, ${primaryColor}15, transparent)` }}
                            >
                              <Sparkles size={32} style={{ color: primaryColor }} className="opacity-40" />
                            </div>
                          )}
                          
                          {/* Date overlay */}
                          <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/70 backdrop-blur-md rounded-lg">
                            <p className="text-xs font-bold uppercase">
                              {event.date ? formatInTimeZone(new Date(event.date), 'America/Argentina/Buenos_Aires', 'd MMM', { locale: es }) : 'TBC'}
                            </p>
                          </div>

                          {/* Past overlay */}
                          {activeTab === 'past' && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <span className="px-3 py-1 bg-white/10 rounded-full text-xs font-semibold">
                                Finalizado
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-5 md:p-6 flex flex-col justify-between min-h-[160px]">
                          <div>
                            <h3 className="text-lg md:text-xl font-bold uppercase tracking-tight mb-2 group-hover:text-white transition-colors line-clamp-2">
                              {event.title}
                            </h3>

                            {/* Co-presented chips */}
                            {event.is_co_produced && Array.isArray(event.co_presenters) && event.co_presenters.length > 0 && (
                              <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid={`event-copresent-${event.id}`}>
                                <span className="text-[9px] uppercase tracking-[0.28em] font-bold text-white/40">
                                  {event.is_primary_owner ? 'Junto a' : 'Con'}
                                </span>
                                {event.co_presenters.slice(0, 3).map((cp) => (
                                  <span
                                    key={cp.producer_id}
                                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5 border border-white/10"
                                    title={cp.name}
                                  >
                                    {cp.logo ? (
                                      <img src={cp.logo} alt={cp.name} className="w-4 h-4 rounded-full object-cover" />
                                    ) : (
                                      <span
                                        className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black"
                                        style={{ background: `${primaryColor}30`, color: primaryColor }}
                                      >
                                        {(cp.name || '?').charAt(0).toUpperCase()}
                                      </span>
                                    )}
                                    <span className="text-[10px] font-bold text-white">{cp.name}</span>
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-white/40 text-sm">
                              {event.location && (
                                <span className="flex items-center gap-1.5">
                                  <MapPin size={12} />
                                  <span className="truncate max-w-[150px]">{event.location}</span>
                                </span>
                              )}
                              {event.time && (
                                <span className="flex items-center gap-1.5">
                                  <Clock size={12} />
                                  {event.time}hs
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Footer */}
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                            {activeTab === 'upcoming' && event.tickets?.length > 0 && (
                              <div className="flex items-center gap-2">
                                <Ticket size={14} style={{ color: primaryColor }} />
                                <span className="text-sm font-semibold" style={{ color: primaryColor }}>
                                  ${Math.min(...event.tickets.map(t => t.price || 0)).toLocaleString()}
                                </span>
                              </div>
                            )}
                            <motion.div 
                              className="flex items-center gap-1 text-sm font-semibold ml-auto"
                              animate={{ x: hoveredEvent === event.id ? 4 : 0 }}
                            >
                              <span style={{ color: primaryColor }}>Ver</span>
                              <ArrowUpRight size={14} style={{ color: primaryColor }} />
                            </motion.div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-24 pb-32 md:pb-24">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative text-center"
          >
            {/* Glow */}
            <div 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full blur-[100px] opacity-20"
              style={{ background: primaryColor }}
            />
            
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] mb-4" style={{ color: primaryColor }}>
                Unite a nosotros
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-black uppercase tracking-tight mb-6">
                ¿Querés ser
                <br />
                <span style={{ color: primaryColor }}>productora</span>?
              </h2>
              <p className="text-white/50 mb-8 max-w-md mx-auto text-sm sm:text-base">
                Si producís eventos y querés aparecer en nuestra plataforma, contactanos.
              </p>
              <Link
                to="/productoras/aplicar"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 font-bold uppercase text-sm text-black rounded-full hover:gap-3 transition-all"
                style={{ background: `linear-gradient(135deg, ${primaryColor}, ${auraColor2})` }}
              >
                Aplicar ahora
                <ChevronRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer - Hidden on mobile */}
      <footer className="hidden md:block border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">
            © {new Date().getFullYear()} {producer.business_name}
          </p>
          {producer.social_links?.length > 0 && (
            <div className="flex items-center gap-4">
              {producer.social_links.map((link, idx) => {
                const config = socialConfig[link.platform] || socialConfig.website;
                const Icon = config.icon;
                return (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/30 hover:text-white transition-colors"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

export default ProducerPublicPage;
